'use client';

import { createPortal } from 'react-dom';
import { useEffect, useMemo, useState } from 'react';
import {
  Check,
  ChevronRight,
  ExternalLink,
  Loader2,
  LocateFixed,
  MapPin,
  Search,
  X,
} from 'lucide-react';
import { ResponsiveSheet, SheetPage } from '@church/nextjs-ui/components/ResponsiveSheet';
import type { CareOffer, CarePlace, FormAnswers, HouseholdMember, ServeForm } from '@/lib/types';
import {
  CareRequestError,
  fetchCareForm,
  fetchHousehold,
  searchPlaces,
  submitCareRequest,
} from '@/lib/api';
import {
  getMpWidgetToken,
  getSelectedCampusId,
  onCampusChange,
  onMpTokenChange,
} from '@/lib/campus';
import { HomeMap, type MapPinSpec } from '@church/nextjs-ui/website/profile';
import { Avatar } from './Avatar';
import { CampusIcon, useCampusSvgMap } from './CampusIcon';
import { FormFields, missingRequired } from './FormFields';
import { campusLabel, fmtDay, fmtTime, parseMp } from '@/lib/format';
import { KICKER } from './PageBand';
import { RichDescription } from './RichDescription';

/**
 * "We'd love a Thanksgiving dinner." The care side of a Collection, and the
 * whole of a /care page.
 *
 * EVERY PLACE FIRST, A TO Z. Before a search the list is every offer in
 * alphabetical order with its date, so nobody has to type anything to see
 * what exists. A city, a zip or "Use my location" re-sorts it by distance and
 * drops any delivery that does not reach there.
 *
 * ONLY WHAT CAN BE ASKED FOR IS PICKABLE. An offer not open yet, full or
 * closed stays in the list, greyed, with why.
 *
 * NO SIGN-IN, ONE PER HOUSEHOLD. The API matches or makes the household and
 * refuses a second request in the same Collection with the one they have, so
 * this page can say where and when instead of just "no".
 *
 * Counts only, never who asked. A full offer stays in the list, marked, so
 * the page can point to one with room.
 */

const FIELD =
  'w-full min-w-0 rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-[#1c2b39] placeholder:text-gray-400 focus:border-[var(--accent,#299c8f)] focus:ring-2 focus:ring-[color-mix(in_srgb,var(--accent,#299c8f)_30%,transparent)] focus:outline-none';
const LABEL = 'text-xs font-bold text-gray-500';
const CTA =
  'inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-[var(--accent,#299c8f)] px-6 py-3 text-base font-bold text-[var(--on-accent,#fff)] shadow-sm transition-all hover:scale-[1.02] hover:shadow-md active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100';

/** Tailwind's lg: below it the request opens in a sheet, at it the card sits beside the list. */
function useDesktop() {
  const [desktop, setDesktop] = useState(true);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const update = () => setDesktop(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  return desktop;
}

/**
 * The signed-in household, or null. A care request is one per household, so
 * there is nobody to pick: the API files it on the viewer's own household and
 * fills the name, phone and email from their record.
 */
function useHousehold(apiBaseUrl: string) {
  const [members, setMembers] = useState<HouseholdMember[] | null>(null);
  useEffect(() => {
    let live = true;
    const load = () => {
      if (!getMpWidgetToken()) {
        setMembers(null);
        return;
      }
      fetchHousehold(apiBaseUrl)
        .then((m) => live && setMembers(m && m.length ? m : null))
        .catch(() => live && setMembers(null));
    };
    load();
    const off = onMpTokenChange(load);
    return () => {
      live = false;
      off();
    };
  }, [apiBaseUrl]);
  return members;
}

function miles(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const r = Math.PI / 180;
  const dLat = (b.lat - a.lat) * r;
  const dLng = (b.lng - a.lng) * r;
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * r) * Math.cos(b.lat * r) * Math.sin(dLng / 2) ** 2;
  return 2 * 3959 * Math.asin(Math.sqrt(h));
}

/** "Sat, Nov 21 · 9:30 AM to 11:30 AM" */
export function careWhen(o: Pick<CareOffer, 'date' | 'end'>): string | null {
  if (!o.date) return null;
  const start = parseMp(o.date);
  const end = o.end ? parseMp(o.end) : null;
  return `${fmtDay(start)} · ${fmtTime(start)}${end ? ` to ${fmtTime(end)}` : ''}`;
}

/** The place, in words a family recognises */
export function carePlace(o: CareOffer): string {
  return campusLabel(o.congregationId, o.congregationName);
}

function placeLine(o: CareOffer): string | null {
  const a = o.address;
  if (!a) return null;
  return (
    [a.line1, [a.city, a.postalCode].filter(Boolean).join(' ')].filter(Boolean).join(', ') || null
  );
}

/** The site an external sign-up lives on, as a family would type it */
function siteName(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return 'their site';
  }
}

/** Why a row can't be picked, or that it is nearly full; nothing when it is simply open */
function stateNote(o: CareOffer): { text: string; tone: 'warn' | 'muted' } | null {
  if (o.externalUrl) return null;
  if (o.state === 'full') return { text: 'Full', tone: 'warn' };
  if (o.state === 'closed') return { text: 'Closed', tone: 'muted' };
  if (o.state === 'upcoming' && o.registrationStart)
    return { text: `Opens ${fmtDay(parseMp(o.registrationStart))}`, tone: 'muted' };
  if (o.remaining !== null && o.remaining <= 15)
    return { text: `${o.remaining} left`, tone: 'warn' };
  return null;
}

/** The website's own browser Maps key (GCP project "Woodside Website"), as profile and event-details use. */
const MAPS_KEY = 'AIzaSyBzf7AATUxLctt3O69OqPVbi3owcCWp17Y';

const TONE = {
  warn: 'text-amber-700',
  muted: 'text-gray-500',
} as const;

export function CarePanel({
  apiBaseUrl,
  offers,
  preselect,
  accentStyle,
  embedded,
  people,
  onDone,
}: {
  apiBaseUrl: string;
  offers: CareOffer[];
  /** Who in the household it is for, as picked before (a Collection's Who) */
  people?: number[];
  /** Embedded: the request went through; the sheet shows it */
  onDone?: (done: CareDone) => void;
  /** An offer the page was opened on (/care/thanksgiving/troy) */
  preselect?: number | null;
  /** The page's accent variables, for the sheet (it renders outside the page) */
  accentStyle?: React.CSSProperties;
  /**
   * Already inside a sheet (a Collection's sign-up): the list, then the
   * picked place's request in its place, its Request button in `submitSlot`.
   */
  embedded?: { submitSlot: HTMLElement | null };
}) {
  const desktop = useDesktop();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const [place, setPlace] = useState<CarePlace | null>(null);
  const point = place ? { lat: place.lat, lng: place.lng } : null;
  // The one zip a search pins down, for the delivery address.
  const zip = place?.zip ?? (place?.zips.length === 1 ? place.zips[0] : '');
  const [selected, setSelected] = useState<number | null>(preselect ?? null);
  // The campus picked in the site header leads the A to Z list until a search.
  const [campus, setCampus] = useState<number | null>(() => getSelectedCampusId());
  useEffect(() => onCampusChange((id) => setCampus(id)), []);

  const rows = useMemo(() => {
    const withDistance = offers
      // A delivery shows until we know the zip, then only where it reaches.
      .filter(
        (o) =>
          !o.isDelivery ||
          !point ||
          o.deliveryPostalCodes.length === 0 ||
          o.deliveryPostalCodes.some((z) => place?.zips.includes(z))
      )
      .map((o) => ({
        o,
        d:
          point && o.lat != null && o.lng != null ? miles(point, { lat: o.lat, lng: o.lng }) : null,
      }));
    return point
      ? withDistance.sort(
          (a, b) => (a.d ?? 9999) - (b.d ?? 9999) || (a.o.date ?? '').localeCompare(b.o.date ?? '')
        )
      : withDistance.sort(
          (a, b) =>
            Number(b.o.congregationId === campus) - Number(a.o.congregationId === campus) ||
            carePlace(a.o).localeCompare(carePlace(b.o)) ||
            Number(a.o.isDelivery) - Number(b.o.isDelivery) ||
            a.o.title.localeCompare(b.o.title)
        );
  }, [offers, place, campus]);

  // A link to an offer that is not open yet lands on the list, where it says when.
  const offer =
    offers.find((o) => o.id === selected && (o.externalUrl || o.state === 'open')) ?? null;
  const nearestFull = point && rows[0] && rows[0].o.state === 'full' ? rows[0] : null;
  const nearestOpen = nearestFull ? rows.find((r) => r.o.state === 'open') : null;

  // One pin per place, in its campus's art; home once there is a zip.
  const svgs = useCampusSvgMap();
  const perCampus = new Map<number, number>();
  for (const o of offers)
    perCampus.set(o.congregationId, (perCampus.get(o.congregationId) ?? 0) + 1);
  const pins: MapPinSpec[] = [
    ...[
      ...new Map(
        offers.filter((o) => o.lat != null && o.lng != null).map((o) => [o.congregationId, o])
      ).values(),
    ].map(
      (o): MapPinSpec => ({
        key: `c${o.congregationId}`,
        kind: 'campus',
        lat: o.lat,
        lng: o.lng,
        title: carePlace(o),
        svgUrl: svgs.get(o.congregationId) ?? null,
      })
    ),
    ...(point
      ? [
          {
            key: 'home',
            kind: 'home' as const,
            lat: point.lat,
            lng: point.lng,
            title: place?.label ?? '',
          },
        ]
      : []),
  ];

  // With a place, frame it and the few closest; without, every place.
  const focus = point
    ? [
        'home',
        ...rows
          .filter((r) => r.d != null)
          .slice(0, 4)
          .map((r) => `c${r.o.congregationId}`),
      ]
    : undefined;

  const list = (
    <div className="flex min-w-0 flex-col gap-3">
      <PlaceSearch apiBaseUrl={apiBaseUrl} onPlace={setPlace} />
      {/* Where they all are, the way the profile shows a household: the
          campuses in their own art, home once there is a place. */}
      <div className="relative h-64 overflow-hidden rounded-[var(--radius-card,1.25rem)] border border-gray-200 shadow-sm sm:h-72">
        <HomeMap
          apiKey={MAPS_KEY}
          pins={pins}
          focusKeys={focus}
          padding={{ top: 48, right: 32, bottom: 32, left: 32 }}
          className="absolute inset-0 h-full w-full"
        />
      </div>

      <div className="grid gap-2">
        {rows.map(({ o, d }) => {
          const note = stateNote(o);
          const blocked = !o.externalUrl && o.state !== 'open';
          const on = selected === o.id;
          const rowClass = `flex w-full items-center gap-3.5 rounded-2xl border bg-white px-4 py-3 text-left no-underline shadow-sm transition-all disabled:cursor-default disabled:opacity-55 ${
            on
              ? 'border-[var(--accent,#299c8f)] ring-2 ring-[var(--accent,#299c8f)]'
              : 'border-gray-200 hover:-translate-y-0.5 hover:shadow-md'
          }`;
          const inner = (
            <>
              <CampusIcon congregationId={o.congregationId} className="h-11 w-11 shrink-0" />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-bold text-[#1c2b39]">
                  {carePlace(o)}
                  {(perCampus.get(o.congregationId) ?? 0) > 1 && (
                    <span className="font-semibold text-gray-500"> · {o.title}</span>
                  )}
                </span>
                <span className="block truncate text-[13px] text-gray-500">
                  {careWhen(o) ?? 'Any time'}
                </span>
                {o.isDelivery && (
                  <span className="mt-1 inline-block rounded-full bg-[var(--accent-soft,#e6f4f2)] px-2 py-0.5 text-[11px] font-bold text-[var(--accent-text,var(--accent,#299c8f))]">
                    Delivery
                  </span>
                )}
              </span>
              <span className="shrink-0 text-right">
                {d != null && (
                  <span className="block text-sm font-extrabold text-[#1c2b39] tabular-nums">
                    {d < 10 ? d.toFixed(1) : Math.round(d)} mi
                  </span>
                )}
                {note && (
                  <span className={`block text-xs font-bold ${TONE[note.tone]}`}>{note.text}</span>
                )}
              </span>
              {!blocked && (
                <ChevronRight
                  className="h-5 w-5 shrink-0 text-[var(--accent-text,var(--accent,#299c8f))]"
                  strokeWidth={2.5}
                />
              )}
            </>
          );
          // An External_URL sign-up lives on another site: the row goes
          // straight there rather than to a panel that only links out.
          return o.externalUrl ? (
            <a key={o.id} href={o.externalUrl} target="_blank" rel="noopener" className={rowClass}>
              {inner}
            </a>
          ) : (
            <button
              key={o.id}
              type="button"
              disabled={blocked}
              aria-pressed={on}
              onClick={() => {
                setSelected(o.id);
                if (!desktop && !embedded) setSheetOpen(true);
              }}
              className={rowClass}
            >
              {inner}
            </button>
          );
        })}
      </div>
      {nearestFull && nearestOpen && (
        <p className="text-sm text-gray-600">
          {carePlace(nearestFull.o)} is full. {carePlace(nearestOpen.o)} has room.
        </p>
      )}
    </div>
  );

  if (embedded) {
    return offer ? (
      <div className="flex flex-col gap-4">
        <button
          type="button"
          onClick={() => setSelected(null)}
          className={`${KICKER} inline-flex w-fit items-center gap-1 text-gray-500 hover:text-[#1c2b39]`}
        >
          <ChevronRight className="h-3.5 w-3.5 rotate-180" strokeWidth={3} />
          All locations
        </button>
        <RequestCard
          apiBaseUrl={apiBaseUrl}
          offer={offer}
          zip={zip}
          people={people}
          onDone={onDone}
          onReset={() => setSelected(null)}
          submitSlot={embedded.submitSlot}
        />
      </div>
    ) : (
      list
    );
  }

  return (
    <div className="grid gap-7 lg:grid-cols-[minmax(0,1fr)_340px]">
      {list}

      {desktop ? (
        <aside className="min-w-0">
          <div className="flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm lg:sticky lg:top-24">
            <RequestCard
              apiBaseUrl={apiBaseUrl}
              offer={offer}
              zip={zip}
              onReset={() => setSelected(null)}
            />
          </div>
        </aside>
      ) : (
        /* On a phone the card would sit below every location, so picking one
           opens it over the list, the way an event's registration does. */
        <ResponsiveSheet
          open={sheetOpen && !!offer}
          onClose={() => setSheetOpen(false)}
          maxWidth="max-w-xl"
          noPanelPadding
          panelClassName="bg-white"
          hideCloseButton
          footer={
            <div
              ref={setSlot}
              className="border-t border-gray-100 bg-white px-5 pt-3 pb-[calc(12px+env(safe-area-inset-bottom,0px))] empty:hidden"
              style={accentStyle}
            />
          }
          header={() =>
            offer ? (
              /* The event sheet's band: the art blurred behind, the poster in
                 front, where and when beside it (EventSheetBand). */
              <div
                className="relative overflow-hidden bg-[color-mix(in_srgb,var(--accent,#1c2b39)_45%,#1c2b39)] text-white"
                style={accentStyle}
              >
                {offer.imageUrl && (
                  <>
                    <img
                      src={offer.imageUrl}
                      alt=""
                      className="pointer-events-none absolute inset-0 h-full w-full scale-110 object-cover blur-2xl"
                    />
                    <div className="absolute inset-0 bg-black/45" />
                  </>
                )}
                <div className="relative flex items-center gap-3 px-5 pt-5 pb-4">
                  {offer.imageUrl && (
                    <img
                      src={offer.imageUrl}
                      alt=""
                      className="aspect-video w-20 shrink-0 rounded-xl object-cover shadow-lg ring-1 ring-white/20"
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-base font-extrabold">{carePlace(offer)}</div>
                    <div className="truncate text-xs text-white/70">
                      {careWhen(offer) ?? offer.title}
                    </div>
                  </div>
                  <button
                    type="button"
                    aria-label="Close"
                    onClick={() => setSheetOpen(false)}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur transition hover:bg-white/25"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ) : null
          }
        >
          <SheetPage name="main">
            <div className="flex flex-col gap-4 p-5 pb-6 text-[#1c2b39]" style={accentStyle}>
              <RequestCard
                apiBaseUrl={apiBaseUrl}
                offer={offer}
                zip={zip}
                onReset={() => {
                  setSelected(null);
                  setSheetOpen(false);
                }}
                submitSlot={slot}
              />
            </div>
          </SheetPage>
        </ResponsiveSheet>
      )}
    </div>
  );
}

/** A care request that went through, for whoever shows it */
export interface CareDone {
  offerId: number;
  names: string[];
  title: string;
  when: string | null;
  place: string | null;
  contactIds: number[];
}

function RequestCard({
  apiBaseUrl,
  offer,
  zip,
  people,
  onReset,
  onDone,
  submitSlot,
}: {
  apiBaseUrl: string;
  offer: CareOffer | null;
  zip: string;
  /** Who it is for, ticked to start with; else just the asker */
  people?: number[];
  /** The sheet takes over on success (the "You're all set" curtain) instead of a done card here */
  onDone?: (done: CareDone) => void;
  onReset: () => void;
  /** A sheet's pinned footer: Request is drawn there so it never scrolls away */
  submitSlot?: HTMLElement | null;
}) {
  const [f, setF] = useState({
    first: '',
    last: '',
    phone: '',
    email: '',
    line1: '',
    city: '',
    zip: '',
  });
  const [website, setWebsite] = useState(''); // honeypot
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{
    when: string | null;
    place: string | null;
    isDelivery: boolean;
  } | null>(null);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setF((cur) => ({ ...cur, [k]: e.target.value }));

  const household = useHousehold(apiBaseUrl);
  const self = household?.find((m) => m.isSelf) ?? null;
  // Signed in with neither a phone nor an email on file: ask for one.
  const needsContact = !!self && !self.phone && !self.email;
  // Who in the household it is for: whoever was picked, else the asker.
  const [forIds, setForIds] = useState<number[] | null>(null);
  const forWho = forIds ?? (people?.length ? people : self ? [self.contactId] : []);
  const toggleFor = (id: number) =>
    setForIds(forWho.includes(id) ? forWho.filter((x) => x !== id) : [...forWho, id]);

  useEffect(() => {
    setError(null);
  }, [offer?.id]);

  // The offer's own questions (Troy's form 2308), with the serve sign-up's renderer.
  const [form, setForm] = useState<ServeForm | null>(null);
  const [answers, setAnswers] = useState<FormAnswers>({});
  useEffect(() => {
    setForm(null);
    setAnswers({});
    if (!offer?.customFormId || offer.externalUrl) return;
    const controller = new AbortController();
    fetchCareForm(apiBaseUrl, offer.id, controller.signal)
      .then((fm) => !controller.signal.aborted && setForm(fm))
      .catch(() => {
        /* the request still goes; the API says if an answer was required */
      });
    return () => controller.abort();
  }, [apiBaseUrl, offer?.id, offer?.customFormId, offer?.externalUrl]);

  if (done && offer) {
    const maps = done.place
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(done.place)}`
      : null;
    return (
      <div className="flex flex-col gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[var(--accent,#299c8f)] text-[var(--on-accent,#fff)]">
          <Check className="h-6 w-6" strokeWidth={3} />
        </span>
        <p className="text-xl font-extrabold">
          You're all set
          {f.first.trim() || self ? `, ${f.first.trim() || (self?.name ?? '').split(' ')[0]}` : ''}.
        </p>
        <div className="rounded-2xl bg-[var(--accent-soft,#e6f4f2)] px-4 py-3">
          <b className="block">
            {carePlace(offer)} · {offer.title}
          </b>
          {done.when && <span className="block text-sm text-gray-600">{done.when}</span>}
          {done.place && <span className="block text-sm text-gray-600">{done.place}</span>}
        </div>
        {offer.description && (
          <RichDescription text={offer.description} className="text-sm text-gray-700" />
        )}
        {maps && !done.isDelivery && (
          <a
            href={maps}
            target="_blank"
            rel="noopener"
            className="inline-flex items-center gap-1.5 text-sm font-bold text-[var(--accent-text,var(--accent,#299c8f))]"
          >
            <MapPin className="h-4 w-4" /> Directions
          </a>
        )}
        {(f.email.trim() || self?.email) && (
          <p className="text-sm text-gray-500">
            We sent the details to {f.email.trim() || self?.email}.
          </p>
        )}
        <button
          type="button"
          onClick={() => {
            setDone(null);
            onReset();
          }}
          className="text-left text-sm font-semibold text-gray-500 hover:text-[#1c2b39]"
        >
          Start over
        </button>
      </div>
    );
  }

  if (!offer) {
    return (
      <>
        <span className={`${KICKER} text-gray-500`}>Your request</span>
        <p className="text-sm text-gray-500">Pick a place to get started.</p>
        <button type="button" disabled className={CTA}>
          Request
        </button>
      </>
    );
  }

  if (offer.externalUrl) {
    return (
      <>
        <span className={`${KICKER} text-gray-500`}>Your request</span>
        <div className="rounded-2xl bg-[var(--accent-soft,#e6f4f2)] px-4 py-3">
          <b className="block">{carePlace(offer)}</b>
          <span className="block text-sm text-gray-600">{careWhen(offer) ?? offer.title}</span>
        </div>
        <a
          href={offer.externalUrl}
          target="_blank"
          rel="noopener"
          className={`${CTA} no-underline`}
        >
          Continue on {siteName(offer.externalUrl)}
          <ExternalLink className="h-4 w-4" strokeWidth={2.5} />
        </a>
      </>
    );
  }

  const requestButton = (
    <button
      type="submit"
      form={`sf-care-${offer.id}`}
      disabled={sending}
      className={`${CTA} w-full`}
    >
      {sending ? 'Sending…' : 'Request'}
      {!sending && <ChevronRight className="h-4 w-4" strokeWidth={2.5} />}
    </button>
  );

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (sending) return;
    if (!self && (!f.first.trim() || !f.last.trim())) return setError('Please tell us your name.');
    if (household && !forWho.length) return setError('Pick who it is for.');
    if ((!self || needsContact) && !f.phone.trim() && !f.email.trim())
      return setError('Please give us a phone number or an email.');
    if (offer.isDelivery && (!f.line1.trim() || !f.city.trim()))
      return setError('Please give us the address to deliver to.');
    if (form && missingRequired(form, answers).length)
      return setError('Please answer the questions marked required.');
    setSending(true);
    setError(null);
    try {
      const result = await submitCareRequest(apiBaseUrl, offer.id, {
        // Signed in, the API reads these from the viewer's own record.
        person: {
          firstName: f.first.trim(),
          lastName: f.last.trim(),
          ...(f.email.trim() ? { email: f.email.trim() } : {}),
          ...(f.phone.trim() ? { phone: f.phone.trim() } : {}),
        },
        ...(offer.isDelivery
          ? {
              address: {
                line1: f.line1.trim(),
                city: f.city.trim(),
                state: 'MI',
                postalCode: (f.zip || zip).trim(),
              },
            }
          : {}),
        ...(household ? { contactIds: forWho } : {}),
        ...(form ? { answers } : {}),
        website,
      });
      if (onDone) {
        const firsts = household
          ? household.filter((m) => forWho.includes(m.contactId)).map((m) => m.name.split(' ')[0])
          : [f.first.trim()];
        onDone({
          offerId: offer.id,
          names: firsts,
          title: `${carePlace(offer)} · ${offer.title}`,
          when: result.offer.when,
          place: result.offer.place,
          contactIds: household ? forWho : [],
        });
        return;
      }
      setDone({
        when: result.offer.when,
        place: result.offer.place,
        isDelivery: result.offer.isDelivery,
      });
    } catch (err) {
      if (err instanceof CareRequestError && err.state === 'already' && err.existing) {
        setError(
          `Your household is already signed up: ${err.existing.title}${err.existing.when ? `, ${err.existing.when}` : ''}.`
        );
      } else {
        setError(err instanceof Error ? err.message : 'We could not save that right now.');
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <form id={`sf-care-${offer.id}`} onSubmit={submit} className="flex flex-col gap-3.5">
      <span className={`${KICKER} text-gray-500`}>Your request</span>
      <div className="rounded-2xl bg-[var(--accent-soft,#e6f4f2)] px-4 py-3">
        <b className="block">
          {carePlace(offer)} · {offer.title}
        </b>
        <span className="block text-sm text-gray-600">{careWhen(offer) ?? 'Any time'}</span>
        {placeLine(offer) && !offer.isDelivery && (
          <span className="block text-sm text-gray-600">{placeLine(offer)}</span>
        )}
      </div>
      {household ? (
        <div className="flex flex-col gap-1">
          <span className={LABEL}>Who is it for?</span>
          <ul className="divide-y divide-gray-100">
            {household.map((m) => {
              const on = forWho.includes(m.contactId);
              return (
                <li key={m.contactId}>
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={on}
                    onClick={() => toggleFor(m.contactId)}
                    className="flex w-full items-center gap-3 py-2 text-left"
                  >
                    <span
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition ${
                        on
                          ? 'border-[var(--accent,#299c8f)] bg-[var(--accent,#299c8f)] text-[var(--on-accent,#fff)]'
                          : 'border-gray-300 bg-white'
                      }`}
                    >
                      {on && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                    </span>
                    <Avatar name={m.name} imageUrl={m.imageUrl} size={32} />
                    <span className="min-w-0 truncate text-sm font-bold">{m.name}</span>
                    <span className="shrink-0 text-[0.7rem] font-semibold text-gray-500">
                      {m.isSelf ? 'You' : m.relationship}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          {needsContact && (
            <label className="mt-2 flex flex-col gap-1">
              <span className={LABEL}>Phone</span>
              <input
                value={f.phone}
                onChange={set('phone')}
                inputMode="tel"
                autoComplete="tel"
                className={FIELD}
              />
            </label>
          )}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2.5">
            <label className="flex min-w-0 flex-col gap-1">
              <span className={LABEL}>First name</span>
              <input
                value={f.first}
                onChange={set('first')}
                autoComplete="given-name"
                className={FIELD}
              />
            </label>
            <label className="flex min-w-0 flex-col gap-1">
              <span className={LABEL}>Last name</span>
              <input
                value={f.last}
                onChange={set('last')}
                autoComplete="family-name"
                className={FIELD}
              />
            </label>
          </div>
          <label className="flex flex-col gap-1">
            <span className={LABEL}>Phone</span>
            <input
              value={f.phone}
              onChange={set('phone')}
              inputMode="tel"
              autoComplete="tel"
              className={FIELD}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className={LABEL}>
              Email <span className="font-normal text-gray-400">optional</span>
            </span>
            <input
              value={f.email}
              onChange={set('email')}
              inputMode="email"
              autoComplete="email"
              className={FIELD}
            />
          </label>
        </>
      )}
      {offer.isDelivery && (
        <>
          <label className="flex flex-col gap-1">
            <span className={LABEL}>Delivery address</span>
            <input
              value={f.line1}
              onChange={set('line1')}
              autoComplete="address-line1"
              className={FIELD}
            />
          </label>
          <div className="grid grid-cols-[1fr_7rem] gap-2.5">
            <label className="flex min-w-0 flex-col gap-1">
              <span className={LABEL}>City</span>
              <input
                value={f.city}
                onChange={set('city')}
                autoComplete="address-level2"
                className={FIELD}
              />
            </label>
            <label className="flex min-w-0 flex-col gap-1">
              <span className={LABEL}>Zip</span>
              <input
                value={f.zip || zip}
                onChange={set('zip')}
                inputMode="numeric"
                autoComplete="postal-code"
                className={FIELD}
              />
            </label>
          </div>
        </>
      )}
      {form && <FormFields form={form} answers={answers} onChange={setAnswers} />}
      <input
        type="text"
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
      />
      {error && <p className="text-sm font-semibold text-amber-700">{error}</p>}
      {submitSlot ? createPortal(requestButton, submitSlot) : requestButton}
    </form>
  );
}

/**
 * "Where are you?": a city or zip typed, or the device's location. A zip
 * resolves as soon as it has five digits; a city lists what it could be as
 * you type. All of it is answered from the church's own addresses
 * (/api/care/places), never an outside geocoder.
 */
function PlaceSearch({
  apiBaseUrl,
  onPlace,
}: {
  apiBaseUrl: string;
  onPlace: (place: CarePlace | null) => void;
}) {
  const [query, setQuery] = useState('');
  // The text of the place picked, so showing it does not search again.
  const [picked, setPicked] = useState<string | null>(null);
  const [options, setOptions] = useState<CarePlace[] | null>(null);
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(false);
  const [locating, setLocating] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const pick = (p: CarePlace, text: string) => {
    setPicked(text);
    setQuery(text);
    setOpen(false);
    setNote(null);
    onPlace(p);
  };
  const clear = () => {
    setQuery('');
    setPicked(null);
    setOptions(null);
    setNote(null);
    onPlace(null);
  };

  useEffect(() => {
    const q = query.trim();
    if (q === picked) return;
    setOptions(null);
    const zip = /^\d{5}$/.test(q);
    if (!zip && !/^[a-zA-Z][a-zA-Z .'-]+$/.test(q)) return;
    const controller = new AbortController();
    const timer = window.setTimeout(
      () => {
        searchPlaces(apiBaseUrl, { q }, controller.signal)
          .then((found) => {
            if (controller.signal.aborted) return;
            if (zip) {
              if (found[0]) pick(found[0], q);
              else setNote("We don't know that zip yet. Pick from the list below.");
              return;
            }
            setOptions(found);
            setActive(0);
            setOpen(true);
          })
          .catch(() => {});
      },
      zip ? 0 : 200
    );
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl, query, picked]);

  const locate = () => {
    if (!navigator.geolocation) {
      setNote('This browser cannot share a location. Type a city or zip instead.');
      return;
    }
    setLocating(true);
    setNote(null);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const here = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        const [near] = await searchPlaces(apiBaseUrl, here).catch(() => []);
        setLocating(false);
        // The exact spot for distances; the nearest zip for deliveries.
        pick(
          { label: 'Your location', zip: near?.zip ?? null, zips: near?.zips ?? [], ...here },
          near ? `Your location · ${near.label}` : 'Your location'
        );
      },
      (err) => {
        setLocating(false);
        setNote(
          err.code === err.PERMISSION_DENIED
            ? 'Location is turned off for this site. Type a city or zip instead.'
            : 'We could not find your location. Type a city or zip instead.'
        );
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 }
    );
  };

  const list = open && options;
  return (
    <div className="relative">
      <div className="flex items-center gap-2 rounded-2xl border border-gray-200 bg-white py-1.5 pr-1.5 pl-4 shadow-sm transition focus-within:border-[var(--accent,#299c8f)] focus-within:ring-2 focus-within:ring-[color-mix(in_srgb,var(--accent,#299c8f)_30%,transparent)]">
        <Search className="h-5 w-5 shrink-0 text-gray-400" strokeWidth={2.5} />
        <input
          type="search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!e.target.value.trim()) clear();
          }}
          onFocus={() => options?.length && setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={(e) => {
            if (!list || !options.length) return;
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setActive((i) => Math.min(i + 1, options.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActive((i) => Math.max(i - 1, 0));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              pick(options[active], options[active].label);
            } else if (e.key === 'Escape') setOpen(false);
          }}
          enterKeyHint="search"
          autoComplete="off"
          placeholder="Search a city or zip"
          aria-label="Search a city or zip"
          role="combobox"
          aria-expanded={!!list}
          className="min-w-0 flex-1 border-0 bg-transparent py-2 text-base font-semibold text-[#1c2b39] placeholder:font-medium placeholder:text-gray-400 focus:outline-none [&::-webkit-search-cancel-button]:hidden"
        />
        {query && (
          <button
            type="button"
            onClick={clear}
            aria-label="Clear"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="h-4 w-4" strokeWidth={2.5} />
          </button>
        )}
        <button
          type="button"
          onClick={locate}
          disabled={locating}
          aria-label="Use my location"
          className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-xl bg-[var(--accent-soft,#e6f4f2)] px-3 text-sm font-bold text-[var(--accent-text,var(--accent,#299c8f))] transition hover:scale-[1.03] hover:shadow-sm active:scale-95 disabled:opacity-60"
        >
          {locating ? (
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.5} />
          ) : (
            <LocateFixed className="h-4 w-4" strokeWidth={2.5} />
          )}
          <span className="hidden sm:inline">Use my location</span>
        </button>
      </div>
      {list && (
        <ul
          role="listbox"
          className="absolute inset-x-0 top-full z-20 mt-1.5 overflow-hidden rounded-2xl border border-white/60 bg-white/90 p-1.5 shadow-lg backdrop-blur-xl"
        >
          {options.length ? (
            options.map((p, i) => (
              <li key={`${p.label}-${i}`}>
                <button
                  type="button"
                  role="option"
                  aria-selected={i === active}
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => pick(p, p.label)}
                  className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-base font-semibold text-[#1c2b39] ${
                    i === active ? 'bg-[var(--accent-soft,#e6f4f2)]' : ''
                  }`}
                >
                  <MapPin className="h-4 w-4 shrink-0 text-[var(--accent-text,var(--accent,#299c8f))]" />
                  {p.label}
                </button>
              </li>
            ))
          ) : (
            <li className="px-3 py-2.5 text-sm font-semibold text-gray-500">No matches</li>
          )}
        </ul>
      )}
      {note && <p className="mt-2 text-sm font-semibold text-amber-700">{note}</p>}
    </div>
  );
}
